import { PT_TIMEZONE } from "./constants";

const dtfCache = {};

const getFormatter = () => {
  if (!dtfCache[PT_TIMEZONE]) {
    dtfCache[PT_TIMEZONE] = new Intl.DateTimeFormat("en-US", {
      timeZone: PT_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      weekday: "short",
    });
  }
  return dtfCache[PT_TIMEZONE];
};

const getParts = (date) => {
  const formatter = getFormatter();
  const parts = formatter.formatToParts(date);
  const map = {};
  parts.forEach((p) => {
    if (p.type !== "literal") {
      map[p.type] = p.value;
    }
  });
  return {
    year: parseInt(map.year, 10),
    month: parseInt(map.month, 10),
    day: parseInt(map.day, 10),
    hour: parseInt(map.hour || "0", 10),
    minute: parseInt(map.minute || "0", 10),
    second: parseInt(map.second || "0", 10),
    weekdayLabel: map.weekday || "",
  };
};

const getOffset = (date) => {
  const { year, month, day, hour, minute, second } = getParts(date);
  const asUTC = Date.UTC(year, month - 1, day, hour, minute, second);
  return asUTC - date.getTime();
};

const pad = (value) => String(value).padStart(2, "0");

export const toDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  if (input.toDate) return input.toDate();
  if (typeof input === "number") return new Date(input);
  if (typeof input === "string") {
    const parsed = Date.parse(input);
    if (!isNaN(parsed)) return new Date(parsed);
  }
  return null;
};

export const startOfDayPT = (input) => {
  const date = toDate(input);
  if (!date) return null;
  const { year, month, day } = getParts(date);
  const utcGuess = Date.UTC(year, month - 1, day, 0, 0, 0);
  const offset = getOffset(new Date(utcGuess));
  return new Date(utcGuess - offset);
};

export const startOfMonthPT = (input) => {
  const date = toDate(input);
  if (!date) return null;
  const { year, month } = getParts(date);
  const utcGuess = Date.UTC(year, month - 1, 1, 0, 0, 0);
  const offset = getOffset(new Date(utcGuess));
  return new Date(utcGuess - offset);
};

export const addMonthsPT = (input, months) => {
  const start = startOfMonthPT(input);
  if (!start) return null;
  const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 12));
  d.setUTCMonth(d.getUTCMonth() + months);
  return startOfMonthPT(d);
};

export const startOfNextMonthPT = (input) => {
  const start = startOfMonthPT(input);
  if (!start) return null;
  const d = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 12));
  d.setUTCMonth(d.getUTCMonth() + 1);
  return startOfMonthPT(d);
};

export const addDaysPT = (input, days) => {
  const start = startOfDayPT(input);
  if (!start || !Number.isFinite(days)) return start;
  const ref = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), 12));
  ref.setUTCDate(ref.getUTCDate() + days);
  return startOfDayPT(ref);
};

export const startOfWeekMondayPT = (input) => {
  const date = toDate(input);
  if (!date) return null;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: PT_TIMEZONE,
    weekday: "short",
  });
  const weekdayLabel = formatter.format(date);
  const labelToIdx = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const idx = labelToIdx[weekdayLabel] ?? 0;
  const daysToMonday = (idx + 6) % 7;
  return addDaysPT(date, -daysToMonday);
};

export const formatDateKeyPT = (input) => {
  const date = toDate(input);
  if (!date) return "";
  const { year, month, day } = getParts(date);
  return `${year}-${pad(month)}-${pad(day)}`;
};

export const formatMonthLabelPT = (input) => {
  const date = toDate(input);
  if (!date) return "";
  const { year, month } = getParts(date);
  return `${year}-${pad(month)}`;
};

export const rangeDaysPT = (start, end) => {
  const items = [];
  let cursor = startOfDayPT(start);
  const limit = toDate(end);
  while (cursor && limit && cursor < limit) {
    items.push(cursor);
    cursor = addDaysPT(cursor, 1);
  }
  return items;
};

export const getPresetRange = (presetKey, customRange = {}) => {
  const todayStart = startOfDayPT(new Date());
  const tomorrow = addDaysPT(todayStart, 1);
  switch (presetKey) {
    case "last7":
      return { start: addDaysPT(tomorrow, -7), end: tomorrow };
    case "last30":
      return { start: addDaysPT(tomorrow, -30), end: tomorrow };
    case "mtd":
      return { start: startOfMonthPT(todayStart), end: tomorrow };
    case "lastFullMonth": {
      const thisMonth = startOfMonthPT(todayStart);
      const lastMonthStart = addMonthsPT(thisMonth, -1);
      return { start: lastMonthStart, end: thisMonth };
    }
    case "custom": {
      const start = startOfDayPT(customRange.start);
      const end = startOfDayPT(customRange.end);
      return start && end && start < end ? { start, end } : { start: todayStart, end: tomorrow };
    }
    default:
      return { start: addDaysPT(tomorrow, -30), end: tomorrow };
  }
};

export const getLastFullMonthRanges = () => {
  const today = startOfDayPT(new Date());
  const currentMonthStart = startOfMonthPT(today);
  const lastMonthStart = addMonthsPT(currentMonthStart, -1);
  const prevMonthStart = addMonthsPT(currentMonthStart, -2);
  return {
    currentMonthStart,
    lastMonthStart,
    lastMonthEnd: currentMonthStart,
    prevMonthStart,
    prevMonthEnd: lastMonthStart,
  };
};

export const isFullMonthRange = (start, end) => {
  const a = startOfMonthPT(start);
  const b = startOfNextMonthPT(start);
  return a && b && a.getTime() === toDate(start)?.getTime() && b.getTime() === toDate(end)?.getTime();
};

export const formatWeekLabel = (date) => {
  const start = startOfWeekMondayPT(date);
  if (!start) return "";
  const d = new Date(start);
  d.setUTCHours(0, 0, 0, 0);
  const dayNumber = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNumber + 3);
  const firstThursday = d.getTime();
  const startOfYear = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const firstWeekDay = (startOfYear.getUTCDay() + 6) % 7;
  startOfYear.setUTCDate(startOfYear.getUTCDate() - firstWeekDay + 3);
  const week = 1 + Math.round((firstThursday - startOfYear.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return `${start.getUTCFullYear()}-W${pad(week)}`;
};

export const clampRange = (range) => {
  const start = startOfDayPT(range?.start);
  const end = startOfDayPT(range?.end);
  if (!start || !end) return null;
  if (start >= end) return null;
  return { start, end };
};
